import React, {useContext, useState, useMemo} from "react";
import {MapContextBuildingArea} from "../../contexts/mapContext";
import { HotelInfoContainer } from "../../components/HotelInfoContainer";
import { A11yStandardType } from "../../models/A11yStandard";
import { AccessibilityWidgetInfoContainer } from "../../components/AccessibilityWidgetInfoContainer";
import { useTranslation } from "react-i18next";
import "../../translations/i18n";
import { BuildArea } from "../../models/BuildArea";
import { Property } from "../../models/Property";
import MapWrapper from "../../components/map/MapWrapper";
import styled from "styled-components";
import {AccessibilityInfo} from "../../models/AccessibilityInfo";
import InformationByDisability from "../../components/InformationByDisabilityContainer";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import BuildingAreaBlockInfo from "./components/BuildingAreaBlockInfo";
import {ReactImageGalleryItem} from "react-image-gallery";
import {ImageGalleryRenderer} from "../../components/image-gallery/ImageGalleryRenderer";
import ImageContainer from "../../components/image-gallery/ImageContainer";
import {useStorageData} from "../../hooks/useStorageData";
import {AccessibilityUnit} from "../../models/AccessibilityUnits";
import {Accessibility} from "../../models/Accessibility";
import InformationMapBlock from "./components/InformationMapBlock";
import MainAccessibility from "../../components/MainAccessibility";
import {Question} from "../../models/Question";
import {getPropertyGeneralImage} from "../../utils/images";

const RootDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const ImageStyled = styled.img`
  object-fit: contain;
  height: 100%;
  background: white;
  border: 1px solid #E3E5E5;
`

export const BuildingAreaPage = (p: {
  buildingArea: BuildArea;
  a11yStandard: A11yStandardType;
  propertiesCount: number;
  shopsCount?: number;
  parkingSpaces: number;
  publicToilets: number;
  toilets: number;
  properties: Property[] | undefined;
  buildingAreaAccessibilityInfo: AccessibilityInfo[] | undefined;
  buildingAreaId: string;
  fullscreen: boolean;
  accessibilityUnits: AccessibilityUnit[];
  accessibilities: Accessibility[];
  questions: Question[] | undefined
}) => {
  const {
    buildingArea,
    a11yStandard,
    propertiesCount,
    shopsCount,
    parkingSpaces,
    publicToilets,
    toilets,
    properties,
    buildingAreaAccessibilityInfo,
    fullscreen,
    buildingAreaId,
    accessibilityUnits,
    accessibilities,
    questions
  } = p;

  const { t } = useTranslation();
  const [showMapBuildingArea] = useContext(MapContextBuildingArea);
  const { height } = useWindowDimensions();
  const disabilityTypes: string[] = useStorageData('disability_types')
  const [currentImageData, setCurrentImageData] = useState<any>({index: 0, id: '', name: ''})

  const carouselImages: ReactImageGalleryItem[] = useMemo(() => {
    if (properties && properties.length > 0 && buildingArea.property_images && buildingArea.property_images.length > 0) {
      return buildingArea.property_images.flatMap((i) => {
        const foundProperty = properties.find((property) => property.id === i);
        if (!foundProperty) return [];

        return [{
          fullscreen: foundProperty.general_image,
          original: getPropertyGeneralImage(foundProperty, 'medium'),
          thumbnail: getPropertyGeneralImage(foundProperty, 'small'),
          renderItem: ImageGalleryRenderer(),
          thumbnailClass: "handiscover-thumbnail-class",
          imageDescription: foundProperty.general_image_description,
          description: foundProperty.general_image_description,
          id: foundProperty.id
        }];
      });
    }
    return [];
  }, [properties, buildingArea.property_images]);

  const handlerCurrentImage = (currentIndex: number) => {
    setCurrentImageData({
      index: currentIndex,
      id: carouselImages[currentIndex].id,
      name: carouselImages[currentIndex].imageDescription
    })
  }

  const exclusiveWidgets = [
    '8f718be6-a4cf-428c-b2b9-3c5046b132c5', // helsingborg
    '78e4e95a-c360-44c4-9e1b-1cad77f95f67' // jarfalla
  ]

  return (
    <RootDiv>
        <BuildingAreaBlockInfo
          a11yStandard={a11yStandard}
          buildingAreaId={buildingAreaId}
          properties={properties}
          propertiesCount={propertiesCount}
          shopsCount={shopsCount}
          parkingSpaces={parkingSpaces}
          publicToilets={publicToilets}
          toilets={toilets}
        />
      <AccessibilityWidgetInfoContainer
        height={height}
        fullscreen={fullscreen}
        buildingAreaId={buildingAreaId}
        showMap={showMapBuildingArea}
        a11yStandard={a11yStandard}
        isBuildingArea={true}
        informationBlockStatusData={false}
      >
        {buildingArea.general_image ? (
          <HotelInfoContainer label={`${t("mainContainer_pictures")}`}>
            <ImageStyled
              src={buildingArea.general_image}
              alt={buildingArea.general_image_description}
            />
          </HotelInfoContainer>
        ) : (
          <ImageContainer
            showThumbnails={false}
            carouselImages={carouselImages}
            propertyImages={properties && properties.length > 0 ? properties.filter((i) => i.general_image) : []}
            onSlide={(currentIndex) => handlerCurrentImage(currentIndex)}
          />
        )}

        <div style={{display: 'flex', flexDirection: 'column'}}>
          {exclusiveWidgets.includes(buildingAreaId) ? (
            <InformationMapBlock id={buildingAreaId} />
          ) : null}
          <MainAccessibility
            currentImageData={undefined}
            accessibilities={accessibilities}
            a11yStandard={a11yStandard}
            propertyItemAccessibilityUnits={accessibilityUnits}
            disabilityTypes={disabilityTypes}
            fullHeight={exclusiveWidgets.includes(buildingAreaId)}
            isBuildingArea={true}
          />
          {!exclusiveWidgets.includes(buildingAreaId) ? (
            <InformationByDisability
              questions={questions}
              accessibilityInfo={buildingAreaAccessibilityInfo}
              dontDisplayHeader={exclusiveWidgets.includes(buildingAreaId)}
            />
          ) :null}
        </div>
        <MapWrapper
          id={buildingAreaId}
          standard={a11yStandard.key}
          // Midpoint formula to calc center of bounding box: c1, c2 = (x1 + x2 / 2), (y1 + y2 / 2)
          mapCenterCoordinates={[
            ((buildingArea.area[0] + buildingArea.area[2]) / 2),
            ((buildingArea.area[1] + buildingArea.area[3]) /2)
          ]}
          activePoiId={''}
          onSetActivePoiId={() => {}}
          poisFromBuildingArea={properties}
          crossButton={false}
          customTitle={t('mainContainer_mapView')}
          selectedPropertyImageFromBuildingArea={currentImageData.id}
        />
      </AccessibilityWidgetInfoContainer>
    </RootDiv>
  );
};
